import React, { useState, useRef } from "react";
import { Container } from "../../../globalstyled";
import { useAccess } from "../../../hooks/useAccess";
import AppLoader from "../../../components/loader";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useMutation } from "@apollo/client";
import { SEND_ROUGH_ESTIMATE_TO_USER, CREATE_LEAD } from "../../../apolloClient/operation/mutations/mutation";
import { toast } from "react-toastify";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { CreateLeadInput } from "../../../types/graphQlQuery.interface";
import { List, ListItem, ListItemText, Typography, Paper } from "@mui/material";
import {
  CalculatorContainer,
  CalculatorHeader,
  CalculatorTitle,
  CalculatorSubtitle,
  CalculatorGrid,
  CalculatorSection,
  SectionTitle,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  CalculateButton,
  ResultsGrid,
  ResultItem,
  ResultLabel,
  ResultValue,
  TotalCostSection,
  TotalCostLabel,
  TotalCostValue,
  SavingsSection,
  SavingsItem,
  SavingsLabel,
  SavingsValue,
  SavingsDescription,
  Disclaimer,
  PlaceholderAmount,
  ResultsWrapper,
  ExportSection,
  ExportButton,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  FormField,
  FieldLabel,
  FieldInput,
  ModalActions
} from "./salesEstimateCalculator.styled";

interface SalesEstimateCalculatorProps { }

export const SalesEstimateCalculator: React.FC<SalesEstimateCalculatorProps> = () => {
  const { loading: accessLoading } = useAccess();
  const [sendRoughEstimate] = useMutation(SEND_ROUGH_ESTIMATE_TO_USER);
  const [createLead] = useMutation(CREATE_LEAD);
  const resultsRef = useRef<HTMLDivElement>(null);
  const resultsContentRef = useRef<HTMLDivElement>(null);

  const [roofArea, setRoofArea] = useState<string>("");
  const [roofComplexity, setRoofComplexity] = useState<string>("");
  const [roofPitch, setRoofPitch] = useState<string>("");
  const [insuranceDeductible, setInsuranceDeductible] = useState<string>("");
  const [hasCalculated, setHasCalculated] = useState<boolean>(false);

  // Export modal states
  const [showSendModal, setShowSendModal] = useState<boolean>(false);
  const [clientEmail, setClientEmail] = useState<string>("");
  const [clientName, setClientName] = useState<string>("");

  // Lead generation modal states
  const [showLeadConfirmModal, setShowLeadConfirmModal] = useState<boolean>(false);
  const [showLeadModal, setShowLeadModal] = useState<boolean>(false);
  const [leadAddress, setLeadAddress] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showPredictions, setShowPredictions] = useState<boolean>(false);

  // Google Places API hook for address autocomplete
  const {
    placePredictions,
    getPlacePredictions,
  } = usePlacesService({
    apiKey: "AIzaSyBzepvXcfi4uJL7LpF-aeKW8FVUdDxTonA",
  });

  const [results, setResults] = useState({
    roofAreaSqFt: 0,
    wasteAreaSqFt: 0,
    glasHomesRate: 0,
    insuranceRate: 0,
    roofComplexityHips: "",
    glasHomesTotal: 0,
    insuranceFairMarket: 0,
    deductibleSaved: 0,
    additionalProceeds: 0,
    totalSavings: 0
  });

  const calculateEstimates = () => {
    const area = parseFloat(roofArea) || 0;
    const deductible = parseFloat(insuranceDeductible) || 0;

    if (area > 0 && roofPitch && roofComplexity) {
      // Determine rates based on roof pitch
      let glasRate = 3.25;
      let insuranceRatePerSqFt = 7.00;

      if (roofPitch === "Low (Less than 7/12)") {
        glasRate = 3.25;
        insuranceRatePerSqFt = 6.50;
      } else if (roofPitch === "Mid 7/12-9/12") {
        glasRate = 3.50;
        insuranceRatePerSqFt = 7.00;
      } else if (roofPitch === "Steep (Greater than 9/12)") {
        glasRate = 3.75;
        insuranceRatePerSqFt = 7.50;
      }

      // Determine complexity multiplier
      let complexityMultiplier = 1.15;
      if (roofComplexity === "Less than 5 Hips") {
        complexityMultiplier = 1.10;
      } else if (roofComplexity === "5-10 Hips") {
        complexityMultiplier = 1.15;
      } else if (roofComplexity === "10+ Hips") {
        complexityMultiplier = 1.20;
      }

      // Calculate using the formula from the specifications
      // Insurance Fair Market Value = (Roof Area) * (Pitch Angle Insurance Price/SQFT) * (Roof Complexity Multiplier)
      const insuranceTotal = area * insuranceRatePerSqFt * complexityMultiplier;

      // Glas Estimated Cost = (Roof Area) * (Pitch Angle Glas Price/SQFT) * (Roof Complexity Multiplier)
      const glasTotal = area * glasRate * complexityMultiplier;

      // Total Savings = (Insurance Estimated Value - Glas Estimated Price)
      const totalSavingsFromPricing = insuranceTotal - glasTotal;

      // Additional Ins Proceeds (Cash in your Pocket) = (Insurance Estimated Values - Deductible - Glas Estimated Price)
      const additionalInsuranceProceeds = insuranceTotal - deductible - glasTotal;

      // Total savings includes deductible saved
      const totalSavingsAmount = totalSavingsFromPricing + deductible;

      setResults({
        roofAreaSqFt: area,
        wasteAreaSqFt: Math.round(area * 1.15), // 15% waste factor
        glasHomesRate: glasRate,
        insuranceRate: insuranceRatePerSqFt,
        roofComplexityHips: roofComplexity,
        glasHomesTotal: glasTotal,
        insuranceFairMarket: insuranceTotal,
        deductibleSaved: deductible,
        additionalProceeds: additionalInsuranceProceeds,
        totalSavings: totalSavingsAmount
      });
      setHasCalculated(true);
    }
  };

  const handleCalculateClick = () => {
    calculateEstimates();
  };


  const getLogoDataUrl = (): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const scale = 4;
          canvas.width = 66 * scale;
          canvas.height = 29 * scale;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/png'));
            return;
          }
        } catch (e) {
          console.error('Error drawing logo to canvas', e);
        }
        resolve('');
      };
      img.onerror = () => resolve('');
      img.src = `${process.env.PUBLIC_URL || ''}/logo.svg`;
    });
  };

  const downloadPDF = async () => {
    if (resultsContentRef.current) {
      try {
        // Temporarily hide export buttons
        const exportSection = document.querySelector('[data-export-section]') as HTMLElement;
        if (exportSection) {
          exportSection.style.display = 'none';
        }

        // Temporarily set a fixed width for consistent PDF rendering
        const originalWidth = resultsContentRef.current.style.width;
        const originalMinWidth = resultsContentRef.current.style.minWidth;

        // Force desktop-like width for PDF capture
        resultsContentRef.current.style.width = '600px';
        resultsContentRef.current.style.minWidth = '600px';

        // Wait a bit for reflow
        await new Promise(resolve => setTimeout(resolve, 100));

        // Capture the results section as an image
        const canvas = await html2canvas(resultsContentRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          useCORS: true,
          width: 600,
          height: resultsContentRef.current.scrollHeight,
          windowWidth: 600,
          windowHeight: resultsContentRef.current.scrollHeight,
          ignoreElements: (element) => {
            return element.hasAttribute('data-html2canvas-ignore');
          }
        });

        // Restore original styles
        resultsContentRef.current.style.width = originalWidth;
        resultsContentRef.current.style.minWidth = originalMinWidth;

        // Restore export buttons
        if (exportSection) {
          exportSection.style.display = 'flex';
        }

        // Create PDF with screenshot
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/png');

        // Load Waybe logo
        const logoDataUrl = await getLogoDataUrl();

        const pageWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const margins = 10; // Margins in mm
        const rightMargin = pageWidth - margins; // 200mm

        // Render header: Logo on Left, Title & Date on Right
        if (logoDataUrl) {
          const logoWidth = 34; // mm
          const logoHeight = (29 / 66) * logoWidth; // ~14.9mm
          pdf.addImage(logoDataUrl, 'PNG', margins, 10, logoWidth, logoHeight);
        }

        // Title and Date (Right Aligned)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(20);
        pdf.setTextColor(15, 23, 42);
        pdf.text('ROOF ESTIMATE', rightMargin, 17, { align: 'right' });

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(100, 116, 139);
        pdf.text(`Generated: ${new Date().toLocaleDateString()}`, rightMargin, 23, { align: 'right' });

        // Header Divider Line
        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.4);
        pdf.line(margins, 27, rightMargin, 27);

        // Calculate image dimensions to fit on page
        const contentStartY = 31;
        const imgWidth = pageWidth - (2 * margins); // 190mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Check if image fits on one page
        const maxImgHeight = pageHeight - contentStartY - 10;

        if (imgHeight <= maxImgHeight) {
          // Fits on one page
          pdf.addImage(imgData, 'PNG', margins, contentStartY, imgWidth, imgHeight);
        } else {
          // Scale down to fit on one page with proper margins
          const scaleFactor = Math.min(
            maxImgHeight / imgHeight,
            0.95 // Max 95% scale to ensure margins
          );
          const scaledWidth = imgWidth * scaleFactor;
          const scaledHeight = imgHeight * scaleFactor;

          // Center the scaled image
          const xOffset = (pageWidth - scaledWidth) / 2;
          pdf.addImage(imgData, 'PNG', xOffset, contentStartY, scaledWidth, scaledHeight);
        }

        // Download the PDF
        pdf.save(`roof-estimate-${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`);
      } catch (error) {
        console.error('Error generating PDF:', error);
      }
    }
  };

  const handleSendToClient = async () => {
    if (!clientName || !clientEmail) {
      toast.error('Please fill in client name and email');
      return;
    }

    try {
      // Prepare mutation variables with properly rounded numbers
      const input = {
        email: clientEmail,
        clientName: clientName,
        roofArea: parseFloat(roofArea),
        roofAreaWithWaste: Math.round(results.wasteAreaSqFt),
        roofComplexity: roofComplexity,
        glasRate: parseFloat(results.glasHomesRate.toFixed(2)),
        insuranceRate: parseFloat(results.insuranceRate.toFixed(2)),
        roofPitch: roofPitch,
        glasTotalCost: Math.round(results.glasHomesTotal),
        insuranceFairMarketValue: Math.round(results.insuranceFairMarket),
        deductibleSaved: Math.round(results.deductibleSaved),
        additionalInsuranceProceeds: Math.round(results.additionalProceeds),
        totalSavings: Math.round(results.totalSavings)
      };

      await sendRoughEstimate({
        variables: { input }
      });

      // Show success message
      toast.success(`Estimate successfully sent!`);

      // Reset form and show lead generation modal
      setShowSendModal(false);

      // Show lead generation confirmation modal after sending to client
      setTimeout(() => {
        setShowLeadConfirmModal(true);
      }, 500);
    } catch (error) {
      console.error('Error sending estimate:', error);
      toast.error('Failed to send estimate. Please try again.');
    }
  };

  // Lead generation helper functions
  const handleAddressInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSearchQuery(value);
    if (value.length > 0) {
      setShowPredictions(true);
      getPlacePredictions({ input: value });
    } else {
      setShowPredictions(false);
    }
  };

  const handlePlaceSelect = (place: any) => {
    setLeadAddress(place);
    setSearchQuery(place.description);
    setShowPredictions(false);
  };

  const parseCityAndState = (address: string) => {
    const regex = /^([\w\s]+?),\s*([\w\s]+?),\s*([\w\s]+)$/;
    const matches = address.match(regex);
    return matches
      ? { city: matches[1].trim(), state: matches[2].trim() }
      : null;
  };

  const handleCreateLead = async () => {
    if (!leadAddress) {
      toast.error("Please select an address");
      return;
    }

    if (!clientName || !clientEmail) {
      toast.error("Client name and email are required");
      return;
    }

    try {
      const cityAndState = parseCityAndState(
        leadAddress.structured_formatting.secondary_text
      );

      if (!cityAndState) {
        console.error("Unable to parse city and state from address");
        return;
      }

      const sanitizedState = cityAndState.state.replace(/[^a-zA-Z]/g, "");

      // Valid states (same as in CreateProject)
      const VALID_STATES = ["UT", "TX", "NE", "IA"];
      if (!VALID_STATES.includes(sanitizedState)) {
        toast.warning("Currently, we cannot move forward with this location. Thanks!");
        return;
      }

      const leadInput: CreateLeadInput = {
        streetAddress: leadAddress.structured_formatting.main_text,
        unit: "",
        city: cityAndState.city,
        zipCode: "10257", // Placeholder for zip code
        state: sanitizedState,
        firstName: clientName.split(' ')[0] || clientName,
        lastName: clientName.split(' ')[1] || "",
        email: clientEmail,
        utm: "sales",
      };

      const response = await createLead({
        variables: { input: leadInput }
      });

      if (response.data?.createLead?.leadId) {
        toast.success(`Lead created successfully for ${clientName}!`);
        setShowLeadModal(false);
        setShowLeadConfirmModal(false);

        // Reset lead form
        setLeadAddress(null);
        setSearchQuery("");
        setShowPredictions(false);
        // Also reset client info after lead creation
        setClientEmail('');
        setClientName('');
      }
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Failed to create lead. Please try again.');
    }
  };

  if (accessLoading) {
    return <AppLoader />;
  }

  return (
    <Container>
      <CalculatorContainer>
        <CalculatorHeader>
          <CalculatorTitle>
            📊 Roof Estimate Calculator
          </CalculatorTitle>
          <CalculatorSubtitle className="text-wrap">
            Compare waybe.ai roof cost vs fair market estimated value for insurance claims
          </CalculatorSubtitle>
        </CalculatorHeader>

        <CalculatorGrid>
          <CalculatorSection>
            <SectionTitle>
              🏠 Roof Measurements & Details
            </SectionTitle>

            <FormGroup>
              <FormLabel>Roof Area (sq ft)</FormLabel>
              <FormInput
                type="number"
                value={roofArea}
                onChange={(e) => setRoofArea(e.target.value)}
                placeholder="Enter roof area"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Roof Complexity</FormLabel>
              <FormSelect
                value={roofComplexity}
                onChange={(e) => setRoofComplexity(e.target.value)}
              >
                <option value="">Select roof complexity</option>
                <option value="Less than 5 Hips">Less than 5 Hips</option>
                <option value="5-10 Hips">5-10 Hips</option>
                <option value="10+ Hips">10+ Hips</option>
              </FormSelect>
            </FormGroup>

            <FormGroup>
              <FormLabel>Roof Pitch</FormLabel>
              <FormSelect
                value={roofPitch}
                onChange={(e) => setRoofPitch(e.target.value)}
              >
                <option value="">Select roof pitch</option>
                <option value="Low (Less than 7/12)">Low (Less than 7/12)</option>
                <option value="Mid 7/12-9/12">Mid 7/12-9/12</option>
                <option value="Steep (Greater than 9/12)">Steep (Greater than 9/12)</option>
              </FormSelect>
            </FormGroup>

            <FormGroup>
              <FormLabel>Insurance Deductible ($)</FormLabel>
              <FormInput
                type="number"
                value={insuranceDeductible}
                onChange={(e) => setInsuranceDeductible(e.target.value)}
                placeholder="Enter deductible amount"
              />
            </FormGroup>

            <CalculateButton onClick={handleCalculateClick}>
              Calculate Estimates
            </CalculateButton>
          </CalculatorSection>

          <CalculatorSection ref={resultsRef}>
            <ResultsWrapper ref={resultsContentRef}>
              <SectionTitle>
                💰 Estimation Results
              </SectionTitle>

              {!hasCalculated ? (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "400px",
                  color: "#9ca3af"
                }}>
                  <div style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.3 }}>🧮</div>
                  <p style={{ fontSize: "16px", textAlign: "center" }}>
                    Enter measurements and click calculate to see estimates
                  </p>
                </div>
              ) : (
                <>
                  <ResultsGrid>
                    <ResultItem>
                      <ResultLabel>Roof Area:</ResultLabel>
                      <ResultValue>
                        {hasCalculated ? `${results.roofAreaSqFt.toLocaleString()} sq ft` : <PlaceholderAmount>0 sq ft</PlaceholderAmount>}
                      </ResultValue>
                    </ResultItem>

                    <ResultItem>
                      <ResultLabel>With 15% Waste:</ResultLabel>
                      <ResultValue>
                        {hasCalculated ? `${results.wasteAreaSqFt.toLocaleString()} sq ft` : <PlaceholderAmount>0 sq ft</PlaceholderAmount>}
                      </ResultValue>
                    </ResultItem>

                    <ResultItem>
                      <ResultLabel>Roof Pitch:</ResultLabel>
                      <ResultValue>
                        {hasCalculated ? roofPitch : <PlaceholderAmount>Not selected</PlaceholderAmount>}
                      </ResultValue>
                    </ResultItem>

                    <ResultItem>
                      <ResultLabel>Roof Complexity:</ResultLabel>
                      <ResultValue>
                        {hasCalculated ? results.roofComplexityHips : <PlaceholderAmount>Not selected</PlaceholderAmount>}
                      </ResultValue>
                    </ResultItem>
                  </ResultsGrid>

                  <TotalCostSection>
                    <TotalCostLabel>Estimated Cost</TotalCostLabel>
                    <TotalCostValue>
                      {hasCalculated ? `$${results.glasHomesTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : <PlaceholderAmount>$0</PlaceholderAmount>}
                    </TotalCostValue>
                    <SavingsDescription style={{ color: '#6b7280', fontSize: '13px' }}>Our roofing cost</SavingsDescription>
                  </TotalCostSection>

                  <TotalCostSection>
                    <TotalCostLabel>Insurance Fair Market Value</TotalCostLabel>
                    <TotalCostValue $insurance={true}>
                      {hasCalculated ? `$${results.insuranceFairMarket.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : <PlaceholderAmount>$0</PlaceholderAmount>}
                    </TotalCostValue>
                    <SavingsDescription className="text-wrap" style={{ color: '#6b7280', fontSize: '13px' }}>Insurance claim estimated value (before deductible)</SavingsDescription>
                  </TotalCostSection>

                  <SavingsSection>
                    <SectionTitle style={{ marginBottom: "16px" }}>Your Savings Breakdown</SectionTitle>

                    <SavingsItem>
                      <SavingsLabel>Deductible</SavingsLabel>
                      <SavingsValue>
                        {hasCalculated ? `$${results.deductibleSaved.toLocaleString()}` : <PlaceholderAmount>$0</PlaceholderAmount>}
                      </SavingsValue>

                    </SavingsItem>

                    <SavingsItem>
                      <SavingsLabel>Additional Insurance Proceeds</SavingsLabel>
                      <SavingsValue $green={true}>
                        {hasCalculated ? `$${results.additionalProceeds.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : <PlaceholderAmount>$0</PlaceholderAmount>}
                      </SavingsValue>
                      <SavingsDescription $green={true}>Cash in your pocket</SavingsDescription>
                    </SavingsItem>

                    <SavingsItem $highlight={true}>
                      <SavingsLabel>Total Savings</SavingsLabel>
                      <SavingsValue $highlight={true}>
                        {hasCalculated ? `$${results.totalSavings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : <PlaceholderAmount>$0</PlaceholderAmount>}
                      </SavingsValue>
                      <SavingsDescription className="text-wrap" $highlight={true}>Combined Project Savings</SavingsDescription>
                    </SavingsItem>
                  </SavingsSection>

                  <Disclaimer>
                    <strong>Disclaimer:</strong> Both calculations include waste factor based on roof complexity using the same algorithm. Insurance fair market values are industry estimates. Actual insurance settlements may vary based on policy terms, adjuster evaluation, and local market conditions.
                  </Disclaimer>
                </>
              )}
            </ResultsWrapper>

            {hasCalculated && (
              <ExportSection data-export-section data-html2canvas-ignore>
                <ExportButton
                  $variant="primary"
                  onClick={downloadPDF}
                >
                  📄 Download Estimate
                </ExportButton>
                <ExportButton
                  $variant="secondary"
                  onClick={() => setShowSendModal(true)}
                >
                  📧 Send to Client
                </ExportButton>
                <ExportButton
                  $variant="secondary"
                  onClick={() => {
                    // If client info is not filled, show lead modal directly
                    // Otherwise show confirmation modal
                    if (clientName || clientEmail) {
                      setShowLeadConfirmModal(true);
                    } else {
                      setShowLeadModal(true);
                    }
                  }}
                >
                  🎯 Generate Lead
                </ExportButton>
              </ExportSection>
            )}
          </CalculatorSection>
        </CalculatorGrid>
      </CalculatorContainer>

      {/* Send to Client Modal */}
      <Modal $isOpen={showSendModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Send Estimate to Client</ModalTitle>
            <CloseButton onClick={() => setShowSendModal(false)}>×</CloseButton>
          </ModalHeader>

          <FormField>
            <FieldLabel>Client Name</FieldLabel>
            <FieldInput
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name"
            />
          </FormField>

          <FormField>
            <FieldLabel>Client Email</FieldLabel>
            <FieldInput
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="Enter client email"
            />
          </FormField>


          <ModalActions>
            <ExportButton
              $variant="secondary"
              onClick={() => setShowSendModal(false)}
            >
              Cancel
            </ExportButton>
            <ExportButton
              $variant="primary"
              onClick={handleSendToClient}
              disabled={!clientName || !clientEmail}
            >
              Send Estimate
            </ExportButton>
          </ModalActions>
        </ModalContent>
      </Modal>

      {/* Lead Confirmation Modal */}
      <Modal $isOpen={showLeadConfirmModal}>
        <ModalContent style={{ minWidth: '400px', maxWidth: '500px', width: '90vw' }}>
          <ModalHeader>
            <ModalTitle>🎯 Generate Lead</ModalTitle>
            <CloseButton onClick={() => setShowLeadConfirmModal(false)}>×</CloseButton>
          </ModalHeader>

          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Typography
              variant="body1"
              style={{
                marginBottom: '20px',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal'
              }}
            >
              Great estimate! Would you like to generate a lead for this potential customer?
            </Typography>
            <Typography
              variant="body2"
              style={{
                color: '#666',
                marginBottom: '30px',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal'
              }}
            >
              This will create a lead record with the customer information and property address.
            </Typography>
          </div>

          <ModalActions>
            <ExportButton
              $variant="secondary"
              onClick={() => setShowLeadConfirmModal(false)}
            >
              Not now
            </ExportButton>
            <ExportButton
              $variant="primary"
              onClick={() => {
                setShowLeadConfirmModal(false);
                setShowLeadModal(true);
              }}
            >
              Yes, Generate Lead
            </ExportButton>
          </ModalActions>
        </ModalContent>
      </Modal>

      {/* Lead Generation Modal */}
      <Modal $isOpen={showLeadModal}>
        <ModalContent style={{ maxWidth: '600px', width: '90vw' }}>
          <ModalHeader>
            <ModalTitle>🏠 Generate Lead</ModalTitle>
            <CloseButton onClick={() => setShowLeadModal(false)}>×</CloseButton>
          </ModalHeader>

          <div style={{ padding: '20px' }}>
            <FormField>
              <FieldLabel>Client Name (from estimate)</FieldLabel>
              <FieldInput
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter client name"
              />
            </FormField>

            <FormField>
              <FieldLabel>Client Email (from estimate)</FieldLabel>
              <FieldInput
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="Enter client email"
              />
            </FormField>

            <FormField>
              <FieldLabel>Property Address</FieldLabel>
              <FieldInput
                type="text"
                value={searchQuery}
                onChange={handleAddressInputChange}
                placeholder="Search for property address..."
                style={{ marginBottom: '10px' }}
              />

              {showPredictions && placePredictions && placePredictions.length > 0 && (
                <Paper
                  elevation={3}
                  style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginBottom: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                >
                  <List style={{ padding: '0' }}>
                    {placePredictions.map((item, index) => (
                      <ListItem
                        key={index}
                        onClick={() => handlePlaceSelect(item)}
                        style={{
                          borderBottom: index < placePredictions.length - 1 ? '1px solid #eee' : 'none',
                          padding: '10px 15px',
                          cursor: 'pointer',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word'
                        }}
                      >
                        <ListItemText
                          primary={item.description}
                          style={{
                            margin: 0,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflow: 'visible'
                          }}
                          primaryTypographyProps={{
                            style: {
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              lineHeight: 1.4
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {leadAddress && !showPredictions && (
                <Typography
                  variant="body2"
                  style={{
                    color: '#28a745',
                    marginTop: '10px',
                    padding: '8px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #d4edda',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                    width: '100%'
                  }}
                >
                  ✓ Selected: {leadAddress.description}
                </Typography>
              )}
            </FormField>
          </div>

          <ModalActions>
            <ExportButton
              $variant="secondary"
              onClick={() => setShowLeadModal(false)}
            >
              Cancel
            </ExportButton>
            <ExportButton
              $variant="primary"
              onClick={handleCreateLead}
              disabled={!leadAddress || !clientName || !clientEmail}
            >
              Create Lead
            </ExportButton>
          </ModalActions>
        </ModalContent>
      </Modal>
    </Container>
  );
};